import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { api, setCsrfToken } from '../api/client.ts'
import type { User } from '../api/types.ts'

interface AuthContextValue {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.me()
      .then(res => {
        setUser(res.user)
        setCsrfToken(res.csrf_token)
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false))
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.login(email, password)
    setUser(res.user)
    setCsrfToken(res.csrf_token)
  }, [])

  const logout = useCallback(async () => {
    await api.logout()
    setUser(null)
    setCsrfToken('')
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
